import { SITUACAO_SUSTENTACAO_ORAL_ELETRONICA } from '@/dominio/SituacaoSustentacaoOralEletronica';
import SpeechToTextIcon from '@/img/material_symbols/SpeechToTextIcon';
import ErrorUtil from '@/util/ErrorUtil';
import {
    AssignmentLate,
    AssignmentLateOutlined,
    Block,
    Cancel,
    Check,
    CleaningServices,
    Download
} from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import ReactComponent from '../../ReactComponent';
import ReprovacaoDocumentoSustentacaoOralDialog from './ReprovacaoDocumentoSustentacaoOralDialog';
import TranscricaoDocumentoSustentacaoOralDialog from './TranscricaoDocumentoSustentacaoOralDialog';
import ModoAssinatura from '@/components/assinatura/ModoAssinatura';
import { enqueueSnackbar } from 'notistack';

const estiloBotoes = { minWidth: 'auto', pl: 0, pr: 1.5 };

class GerenciarSustentacoesOraisDialog extends ReactComponent {
    processandoMudancas = false;
    openReprovacaoDocumentoSustentacaoOralDialog = false;
    openTranscricaoDocumentoSustentacaoOralDialog = false;
    idInvalidacaoDocumento;
    idDocumentoSelecionado;
    modalChaveOtpOpen = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            processandoMudancas: observable,
            openReprovacaoDocumentoSustentacaoOralDialog: observable,
            openTranscricaoDocumentoSustentacaoOralDialog: observable,
            idInvalidacaoDocumento: observable,
            idDocumentoSelecionado: observable,
            registro: computed,
            documentos: computed,
            temDocumentos: computed,
            processarAcao: action,
            processarTranscricao: action,
            handleAprovar: action,
            handleAbrirModalReprovacao: action,
            handleReprovar: action,
            handleLimparMarcacao: action,
            handleDownload: action,
            handleSalvarTranscricao: action,
            handleExcluirTranscricao: action,
            handleAbrirModalTranscricao: action,
            handleFecharModalTranscricao: action,
            handleCloseTranscricaoDocumentoSustentacaoOralDialog: action,
            handleCloseReprovacaoDocumentoSustentacaoOralDialog: action,
            handleDownloadSustentacaoOralEletronica: action,
            handleDownloadCertidaoNaoAtendimentoDasExigencias: action,
            handleConfirmarGeracaoCertidaoNaoAtendimentoExigenciasPorProcesso: action,
            handleConfirmarGeracaoCertidaoNaoAtendimentoExigencias: action,
            existeAlgumDocumentoIrregularSemCertidao: computed,
            modalChaveOtpOpen: observable
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get documentos() {
        const listaDocumentos = [];
        this.registro.detalhes?.inscricoes?.forEach((inscricao) => {
            inscricao?.documentos?.forEach(
                ({ id, nome, dataDocumento, tipoDocumento, documentoValidacao, excluido, dataExclusao }) => {
                    const temTranscricao = this.temTranscricaoCadastrada(id);

                    listaDocumentos.push({
                        id,
                        nomeAdvogado: inscricao?.advogado?.nome,
                        nomeDocumento: nome,
                        dataDocumento: dataDocumento
                            ? moment(dataDocumento, 'DD-MM-YYYY-HH-mm-ss').format('DD/MM/YYYY HH:mm')
                            : '-',
                        situacaoAtual: this.getSituacaoValidacao(documentoValidacao),
                        dataValidacao: documentoValidacao?.dataValidacao
                            ? moment(documentoValidacao?.dataValidacao, 'DD-MM-YYYY-HH-mm-ss').format(
                                  'DD/MM/YYYY HH:mm'
                              )
                            : '-',
                        tipoDocumento: tipoDocumento?.id,
                        validado:
                            documentoValidacao?.validado === true || documentoValidacao?.validado === false
                                ? documentoValidacao?.validado
                                : null,
                        excluido,
                        dataExclusao,
                        transcricaoDisponivel: temTranscricao,
                        codigoCertidaoOrigem: documentoValidacao?.codigoCertidaoOrigem
                    });
                }
            );
        });

        return listaDocumentos;
    }

    temTranscricaoCadastrada(idDocumento) {
        return !!(
            this.registro.pedidoSustentacaoOral?.pedidosDetalhados &&
            this.#temTranscricaoSustentacaoOralEletronica(this.registro, idDocumento)
        );
    }

    #temTranscricaoSustentacaoOralEletronica(registro, idDocumento) {
        return registro.pedidoSustentacaoOral.pedidosDetalhados.some((pedido) => {
            return pedido.advogados?.some((advogado) => {
                return advogado.documentosSustentacao?.some((arquivo) => {
                    return !!(
                        arquivo.codigo === idDocumento &&
                        (arquivo.transcricaoDisponivel === true || arquivo.transcricaoDisponivel === 'true')
                    );
                });
            });
        });
    }

    get temDocumentos() {
        return this.documentos && this.documentos.length > 0;
    }

    getSituacaoValidacao(documentoValidacao) {
        if (documentoValidacao?.validado === true) {
            return (
                <Typography sx={{ borderRadius: 1, background: green[300], color: 'white' }}>
                    {SITUACAO_SUSTENTACAO_ORAL_ELETRONICA.APROVADA.descricao}
                </Typography>
            );
        } else if (documentoValidacao?.validado === false) {
            return (
                <Tooltip
                    title={
                        documentoValidacao?.motivoReprovacao
                            ? `Motivo da reprovação: ${documentoValidacao?.motivoReprovacao}`
                            : ''
                    }
                    placement="top-start"
                >
                    <Typography sx={{ borderRadius: 1, background: red[300], color: 'white' }}>
                        {SITUACAO_SUSTENTACAO_ORAL_ELETRONICA.REPROVADA.descricao}
                    </Typography>
                </Tooltip>
            );
        } else {
            return (
                <Typography sx={{ borderRadius: 1, background: grey[400], color: 'white', px: 1 }}>
                    {SITUACAO_SUSTENTACAO_ORAL_ELETRONICA.SEM_VALIDACAO.descricao}
                </Typography>
            );
        }
    }

    processarAcao = async (tipoAlteracao, id, motivo = null) => {
        this.processandoMudancas = true;

        switch (tipoAlteracao) {
            case 'aprovar':
                await this.sessaoService.aprovarSustentacaoOral(id);
                break;
            case 'reprovar':
                await this.sessaoService.reprovarSustentacaoOral(id, motivo);
                break;
            case 'limpar':
                await this.sessaoService.limparMarcacaoSustentacaoOral(id);
                break;
            default:
                break;
        }

        await this.recarregarProcesso();
    };

    processarTranscricao = async (tipoAlteracao, id, textoTranscricao = undefined, textoObservacao = undefined) => {
        this.processandoMudancas = true;

        switch (tipoAlteracao) {
            case 'salvar':
                await this.sessaoService.salvarTranscricaoSustentacaoOralEletronica(
                    id,
                    textoTranscricao,
                    textoObservacao
                );
                break;
            case 'excluir':
                await this.sessaoService.excluirTranscricaoSustentacaoOralEletronica(id);
                break;
            default:
                break;
        }

        await this.recarregarProcesso();
    };

    async recarregarProcesso() {
        try {
            await this.sessaoStore.recarregarProcesso(this.registro);
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(
                `Falha no recarregamento dos dados do processo: ${ErrorUtil.getMensagemErro(error)}`
            );
        } finally {
            runInAction(() => {
                this.processandoMudancas = false;
            });
        }
    }

    handleAprovar = async (id) => {
        await this.processarAcao('aprovar', id);
    };

    handleAbrirModalReprovacao = async (id) => {
        this.idInvalidacaoDocumento = id;
        this.openReprovacaoDocumentoSustentacaoOralDialog = true;
    };

    handleReprovar = async (motivoReprovacao) => {
        this.openReprovacaoDocumentoSustentacaoOralDialog = false;
        await this.processarAcao('reprovar', this.idInvalidacaoDocumento, motivoReprovacao);
    };

    handleSalvarTranscricao = async (textoTranscricao, textoObservacao) => {
        try {
            await this.processarTranscricao('salvar', this.idDocumentoSelecionado, textoTranscricao, textoObservacao);
            runInAction(() => {
                this.openTranscricaoDocumentoSustentacaoOralDialog = false;
            });
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(
                `Falha ao tentar gravar os dados da transcrição: ${ErrorUtil.getMensagemErro(error)}`
            );
        }
    };

    handleExcluirTranscricao = async () => {
        try {
            await this.processarTranscricao('excluir', this.idDocumentoSelecionado);
            runInAction(() => {
                this.openTranscricaoDocumentoSustentacaoOralDialog = false;
            });
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(
                `Falha ao tentar excluir os dados da transcrição: ${ErrorUtil.getMensagemErro(error)}`
            );
        }
    };

    handleAbrirModalTranscricao = (id) => {
        this.idDocumentoSelecionado = id;
        this.openTranscricaoDocumentoSustentacaoOralDialog = true;
    };

    handleFecharModalTranscricao = () => {
        this.openTranscricaoDocumentoSustentacaoOralDialog = false;
    };

    handleLimparMarcacao = async (id) => {
        await this.processarAcao('limpar', id);
    };

    handleDownload = async (conteudo, nome) => {
        const href = URL.createObjectURL(conteudo);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', nome);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    };

    handleCloseTranscricaoDocumentoSustentacaoOralDialog = () => {
        this.openTranscricaoDocumentoSustentacaoOralDialog = false;
    };

    handleCloseReprovacaoDocumentoSustentacaoOralDialog = () => {
        this.openReprovacaoDocumentoSustentacaoOralDialog = false;
    };

    handleDownloadSustentacaoOralEletronica = async (id, nome) => {
        enqueueSnackbar({
            variant: 'warning',
            message: 'Em conformidade com a LGPD, o conteúdo da sustentação oral enviado não constitui peça processual. É vedada sua gravação externa, sem prévia autorização, da tela ou do conteúdo exibido por meio de outros dispositivos, bem como a edição (cortes) do material original.',
            persist: true
        });
        const response = await this.sessaoService.downloadDocumento(id);
        this.handleDownload(response, nome);
    };

    handleDownloadCertidaoNaoAtendimentoDasExigencias = async (id, nome) => {
        const response = await this.sessaoService.downloadCertidaoNaoAtendimentoExigencias(
            this.registro.sessaoId,
            this.registro.processo.numeroCompleto,
            id
        );
        this.handleDownload(response.data, response.headers['content-disposition'].match(/filename="(.*)"/)[1]);
    };

    handleConfirmarGeracaoCertidaoNaoAtendimentoExigenciasPorProcesso = () => {
        this.modalChaveOtpOpen = true;
    };

    handleConfirmarGeracaoCertidaoNaoAtendimentoExigencias = (id) => {
        this.idDocumentoSelecionado = id;
        this.modalChaveOtpOpen = true;
    };

    get existeAlgumDocumentoIrregularSemCertidao() {
        return !this.documentos.some(
            ({ validado, excluido, codigoCertidaoOrigem }) => validado === false && !excluido && !codigoCertidaoOrigem
        );
    }

    getCorBotaoTranscricao = (transcricaoDisponivel, documentoExcluido) => {
        if (this.processandoMudancas) {
            return grey[500];
        }

        if (documentoExcluido === true || documentoExcluido === 'true') {
            return red[500];
        }

        if (transcricaoDisponivel === true || transcricaoDisponivel === 'true') {
            return green[500];
        }

        return 'black';
    };

    callbackContinuarAssinaturaOtp = async (chaveOtp) => {
        this.modalChaveOtpOpen = false;
        if (!chaveOtp || chaveOtp.length !== 6) {
            this.mensagemStore.abrirMensagemAlerta('Código de autorização JTe inválido');
            return;
        }
        try {
            if (this.idDocumentoSelecionado) {
                await this.sessaoService.gerarCertidaoNaoAtendimentoExigencias(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    this.idDocumentoSelecionado,
                    chaveOtp
                );
                this.mensagemStore.abrirMensagemSucesso('Certidão gerada com sucesso.');
            } else {
                await this.sessaoService.gerarCertidaoNaoAtendimentoExigenciasPorProcesso(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    chaveOtp
                );
                this.mensagemStore.abrirMensagemSucesso('Certidões geradas com sucesso.');
            }
            await this.sessaoStore.recarregarProcesso(this.registro);
        } catch (err) {
            console.error(err);
            this.mensagemStore.abrirMensagemAlerta('Erro ao gerar certidão. Tente novamente.');
        }
        this.idDocumentoSelecionado = null;
        this.modalChaveOtpOpen = false;
    };

    callbackCancelar = () => {
        this.modalChaveOtpOpen = false;
        this.idDocumentoSelecionado = null;
    };

    render() {
        return (
            <>
                <Dialog
                    open={true}
                    onClose={this.observableProps.handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    maxWidth="xl"
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mr: 3 }}>
                        <DialogTitle id="alert-dialog-title">
                            Sustentações orais eletrônicas do processo {this.registro.numeroProcesso}
                        </DialogTitle>
                        <Tooltip
                            title={
                                this.existeAlgumDocumentoIrregularSemCertidao
                                    ? 'Não existe nenhum documento em situação Irregular sem certidão gerada.'
                                    : 'Gerar certidão de não atendimento das exigências para todas as sustentações orais do processo'
                            }
                            placement="top-start"
                        >
                            <span>
                                <Button
                                    onClick={() =>
                                        this.handleConfirmarGeracaoCertidaoNaoAtendimentoExigenciasPorProcesso()
                                    }
                                    startIcon={<AssignmentLateOutlined />}
                                    variant="outlined"
                                    color="warning"
                                    disabled={this.existeAlgumDocumentoIrregularSemCertidao || this.processandoMudancas}
                                >
                                    Gerar certidão de não atendimento em lote
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                    <DialogContent>
                        {this.temDocumentos ? (
                            <TableContainer component={Paper}>
                                <Table aria-label="Sustentações orais eletrônicas">
                                    <TableHead sx={{ background: grey[300] }}>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid rgba(204, 204, 204, 1)'
                                                }}
                                            >
                                                Nome do advogado
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid rgba(204, 204, 204, 1)'
                                                }}
                                            >
                                                Nome do arquivo
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid rgba(204, 204, 204, 1)'
                                                }}
                                            >
                                                Data de envio
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid rgba(204, 204, 204, 1)'
                                                }}
                                            >
                                                Situação atual
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    borderRight: '1px solid rgba(204, 204, 204, 1)'
                                                }}
                                            >
                                                Data da validação
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center'
                                                }}
                                            >
                                                Ações
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {this.documentos.map(
                                            ({
                                                id,
                                                nomeAdvogado,
                                                nomeDocumento,
                                                dataDocumento,
                                                situacaoAtual,
                                                dataValidacao,
                                                validado,
                                                excluido,
                                                dataExclusao,
                                                transcricaoDisponivel,
                                                codigoCertidaoOrigem
                                            }) => (
                                                <TableRow
                                                    key={id}
                                                    sx={{
                                                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                                        '&:nth-of-type(odd)': {
                                                            backgroundColor: grey[100]
                                                        },
                                                        '&:last-child td, &:last-child th': { borderBottom: 0 }
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: 0,
                                                            borderRight: '1px solid rgba(224, 224, 224, 1)'
                                                        }}
                                                    >
                                                        {nomeAdvogado}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: 0,
                                                            borderRight: '1px solid rgba(224, 224, 224, 1)'
                                                        }}
                                                    >
                                                        {excluido === true ? (
                                                            <Tooltip
                                                                title={
                                                                    <Typography>
                                                                        {`Excluído pelo adv. ${
                                                                            dataExclusao
                                                                                ? `em ${moment(
                                                                                      dataExclusao,
                                                                                      'DD-MM-YYYY-HH-mm-ss'
                                                                                  ).format('DD/MM/YYYY [às] HH:mm:ss')}`
                                                                                : ''
                                                                        }`}
                                                                    </Typography>
                                                                }
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        textDecoration: excluido ? 'line-through' : ''
                                                                    }}
                                                                >
                                                                    {nomeDocumento}
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : (
                                                            <Typography>{nomeDocumento}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: 0,
                                                            borderRight: '1px solid rgba(224, 224, 224, 1)'
                                                        }}
                                                    >
                                                        {dataDocumento}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            border: 0,
                                                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {situacaoAtual}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: 0,
                                                            borderRight: '1px solid rgba(224, 224, 224, 1)'
                                                        }}
                                                    >
                                                        {dataValidacao}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: 0
                                                        }}
                                                    >
                                                        {excluido ? (
                                                            <Typography>-</Typography>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    gap: 1
                                                                }}
                                                            >
                                                                <Tooltip
                                                                    title={
                                                                        <Typography>
                                                                            Fazer download do arquivo
                                                                        </Typography>
                                                                    }
                                                                    placement="top-start"
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            onClick={() =>
                                                                                this.handleDownloadSustentacaoOralEletronica(
                                                                                    id,
                                                                                    nomeDocumento
                                                                                )
                                                                            }
                                                                            endIcon={<Download />}
                                                                            color="primary"
                                                                            sx={estiloBotoes}
                                                                            disabled={this.processandoMudancas}
                                                                        />
                                                                    </span>
                                                                </Tooltip>

                                                                {this.isComponenteHabilitado(
                                                                    'TranscricaoSustentacaoOralEletronica'
                                                                ) && (
                                                                    <Tooltip
                                                                        title={
                                                                            <Typography>
                                                                                Transcrição do arquivo de áudio/vídeo
                                                                            </Typography>
                                                                        }
                                                                        placement="top-start"
                                                                    >
                                                                        <span>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    this.handleAbrirModalTranscricao(id)
                                                                                }
                                                                                endIcon={
                                                                                    <SpeechToTextIcon
                                                                                        sx={{
                                                                                            fill: this.getCorBotaoTranscricao(
                                                                                                transcricaoDisponivel,
                                                                                                excluido
                                                                                            )
                                                                                        }}
                                                                                    />
                                                                                }
                                                                                sx={estiloBotoes}
                                                                                disabled={this.processandoMudancas}
                                                                            />
                                                                        </span>
                                                                    </Tooltip>
                                                                )}

                                                                <Tooltip
                                                                    title={
                                                                        <Typography>
                                                                            Aprovar sustentação oral eletrônica
                                                                        </Typography>
                                                                    }
                                                                    placement="top-start"
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            onClick={() => this.handleAprovar(id)}
                                                                            endIcon={<Check />}
                                                                            sx={estiloBotoes}
                                                                            color="success"
                                                                            disabled={
                                                                                validado === true ||
                                                                                this.processandoMudancas
                                                                            }
                                                                        />
                                                                    </span>
                                                                </Tooltip>

                                                                <Tooltip
                                                                    title={
                                                                        <Typography>
                                                                            Reprovar sustentação oral eletrônica
                                                                        </Typography>
                                                                    }
                                                                    placement="top-start"
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            onClick={() =>
                                                                                this.handleAbrirModalReprovacao(id)
                                                                            }
                                                                            endIcon={<Block />}
                                                                            sx={estiloBotoes}
                                                                            color="secondary"
                                                                            disabled={
                                                                                validado === false ||
                                                                                this.processandoMudancas
                                                                            }
                                                                        />
                                                                    </span>
                                                                </Tooltip>

                                                                <Tooltip
                                                                    title={
                                                                        <Typography>
                                                                            Limpar marcação de situação da sustentação
                                                                            oral virtual
                                                                        </Typography>
                                                                    }
                                                                    placement="top-start"
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            onClick={() =>
                                                                                this.handleLimparMarcacao(id)
                                                                            }
                                                                            endIcon={<CleaningServices />}
                                                                            color="primary"
                                                                            sx={estiloBotoes}
                                                                            disabled={
                                                                                validado === null ||
                                                                                this.processandoMudancas
                                                                            }
                                                                        />
                                                                    </span>
                                                                </Tooltip>

                                                                {validado === false && (
                                                                    <Tooltip
                                                                        title={
                                                                            <Typography>
                                                                                {`${codigoCertidaoOrigem ? 'Download da' : 'Gerar'} certidão de não atendimentos das exigências`}
                                                                            </Typography>
                                                                        }
                                                                        placement="top-start"
                                                                    >
                                                                        <span>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    codigoCertidaoOrigem
                                                                                        ? this.handleDownloadCertidaoNaoAtendimentoDasExigencias(
                                                                                              codigoCertidaoOrigem,
                                                                                              nomeDocumento
                                                                                          )
                                                                                        : this.handleConfirmarGeracaoCertidaoNaoAtendimentoExigencias(
                                                                                              id
                                                                                          )
                                                                                }
                                                                                endIcon={
                                                                                    codigoCertidaoOrigem ? (
                                                                                        <AssignmentLate />
                                                                                    ) : (
                                                                                        <AssignmentLateOutlined />
                                                                                    )
                                                                                }
                                                                                color="warning"
                                                                                sx={estiloBotoes}
                                                                            />
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                Não existem sustentações orais eletrônicas disponíveis.
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mx: 2 }}>
                        <Button
                            color="secondary"
                            onClick={this.observableProps.handleCloseDialog}
                            variant="outlined"
                            data-testid="closeDialog"
                            endIcon={<Cancel />}
                        >
                            Fechar
                        </Button>

                        {this.processandoMudancas && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <CircularProgress
                                    sx={{ margin: '10px' }}
                                    disableShrink={true}
                                    size="16px"
                                />
                                <Typography>Processando...</Typography>
                            </Box>
                        )}
                    </DialogActions>
                    <ModoAssinatura
                        modalChaveOtpOpen={this.modalChaveOtpOpen}
                        callbackCancelar={this.callbackCancelar}
                        callbackContinuarAssinaturaOtp={this.callbackContinuarAssinaturaOtp}
                        registro={this.registro}
                    />
                </Dialog>

                {this.openTranscricaoDocumentoSustentacaoOralDialog && (
                    <TranscricaoDocumentoSustentacaoOralDialog
                        idDocumento={this.idDocumentoSelecionado}
                        open={this.openTranscricaoDocumentoSustentacaoOralDialog}
                        onSalvarTranscricaoDocumento={this.handleSalvarTranscricao}
                        onExcluirTranscricaoDocumento={this.handleExcluirTranscricao}
                        onClose={this.handleCloseTranscricaoDocumentoSustentacaoOralDialog}
                    />
                )}

                {this.openReprovacaoDocumentoSustentacaoOralDialog && (
                    <ReprovacaoDocumentoSustentacaoOralDialog
                        onReprovarDocumento={this.handleReprovar}
                        onClose={this.handleCloseReprovacaoDocumentoSustentacaoOralDialog}
                    />
                )}
            </>
        );
    }
}

export default inject('stores')(observer(GerenciarSustentacoesOraisDialog));
